/**
 * Placing a React element inside a localized sentence.
 *
 * `t()` returns a string, so a sentence that has to contain a real element — a `<Link>`, most
 * often — cannot be built by interpolation alone. Splitting the copy into "before" and "after"
 * resources is what this avoids: a translator shown two half-sentences cannot see the whole
 * sentence, and cannot move the element to where the target grammar needs it. So the sentence stays
 * in ONE resource with a normal ICU parameter for the element's position.
 *
 * ```tsx
 * // resource: "You can also check status on {trackLink}."
 * const [before, after] = splitAroundSlot(t('...', { trackLink: INLINE_SLOT }));
 * return <>{before}<Link …>{t('…-link-text')}</Link>{after}</>;
 * ```
 */

/** U+0001 (START OF HEADING): a control character cannot occur in translated copy, so the split
 * can never land inside real text. */
export const INLINE_SLOT = '\u0001';

/** Always returns exactly two strings. A translation that drops the placeholder yields
 * `[wholeSentence, '']`, so only the element's position is lost — never the copy. */
export const splitAroundSlot = (formatted: string): [string, string] => {
  const index = formatted.indexOf(INLINE_SLOT);
  if (index === -1) {
    return [formatted, ''];
  }
  return [
    formatted.slice(0, index),
    formatted.slice(index + INLINE_SLOT.length),
  ];
};
